import React, { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';

export type NumberInputProps = Partial<{
    suffix: string;
    name: string;
    value: string | number;
    onChange: (e: ChangeEvent<HTMLInputElement>, value: string | number) => void;
    onBlur: (e: FocusEvent<HTMLInputElement, Element>, value: string | number) => void;
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>, value: string | number) => void;
    disabled: boolean;
    decimalPlaces?: number;
}>;

const NumberInput = ({ suffix, ...props }: NumberInputProps) => {
    const removeNonDigitCharacters = (val: string) => val.replace(/[^.\d]/g, '');
    const parseValue = (val: string) => {
        let parsed = parseFloat(val) || 0;

        if (typeof props.decimalPlaces === 'number') {
            parsed = parseFloat(parsed.toFixed(props.decimalPlaces));
        }

        return parsed;
    };

    const formatValue = (val: string | number) => {
        const parsed =
            typeof val === 'number' && typeof props.decimalPlaces === 'number' ? val.toFixed(props.decimalPlaces) : val;

        if (!suffix) {
            return parsed;
        }

        return String(parsed).includes(suffix) ? parsed : `${parsed}${suffix}`;
    };

    const moveCaret = (input: HTMLInputElement) => {
        if (input.selectionStart === input.value.length && suffix) {
            const newPosition = input.value.length - suffix.length;
            input.setSelectionRange(newPosition, newPosition);
        }
    };

    return (
        <input
            {...props}
            value={
                typeof props.value === 'string' || typeof props.value === 'number'
                    ? formatValue(props.value)
                    : props.value
            }
            onKeyDown={(e) => {
                moveCaret(e.currentTarget);
                if (typeof props.onKeyDown === 'function') {
                    props.onKeyDown(e, parseValue(removeNonDigitCharacters(e.currentTarget.value)));
                }
            }}
            onClick={(e) => moveCaret(e.currentTarget)}
            onChange={(e) => {
                if (typeof props.onChange !== 'function') {
                    return;
                }

                let value = e.target.value;
                if (suffix && !value.includes(suffix)) {
                    value = value.slice(0, -1);
                }

                props.onChange(e, removeNonDigitCharacters(value));
            }}
            onBlur={(e) => {
                if (typeof props.onBlur !== 'function') {
                    return;
                }

                props.onBlur(e, parseValue(removeNonDigitCharacters(e.target.value)));
            }}
        />
    );
};

export default NumberInput;
